<script setup>
import { computed } from 'vue'
import { Badge, Table } from '@drama/ui'

import AuditCard from './AuditCard.vue'
import AuditId from './AuditId.vue'
import { clip, clockTime, duration, elapsedMs, fullTime, useAuditText, variantOf } from './audit-text.js'
import { callTotals } from './run-observer.js'

const props = defineProps({
  // 当前分支最近一次回合请求（快照 requests 的一项），还没有请求时为 null。
  request: { type: Object, default: null },
  // modelStages() 的结果：快照阶段记录并上平台审计。
  stages: { type: Array, required: true },
})

const { tr, enumLabel, errorLabel, costLabel } = useAuditText()
const columns = computed(() => [
  { key: 'task', label: tr('request.stage'), width: '220px' },
  { key: 'model', label: tr('request.model'), width: '320px' },
  { key: 'elapsed', label: tr('request.elapsed'), width: '120px', align: 'right', mono: true },
  { key: 'status', label: tr('request.status'), width: '300px' },
])
const input = computed(() => {
  const submitted = props.request?.submitted_input
  if (!submitted) return '—'
  const mode = enumLabel('turns.modes', submitted.mode)
  return submitted.text ? `${mode} · ${submitted.text}` : mode
})
// 成本 = 各阶段平台记下的供应商成本 + 这次请求向玩家扣的积分。
const cost = computed(() => {
  const totals = callTotals(props.stages.map((stage) => stage.platform).filter(Boolean))
  return costLabel({ cost_micros: totals.cost_micros, charged_points: props.request?.charged_points ?? 0 })
})
const hint = computed(() => (props.request && !Array.isArray(props.request.audits) ? tr('request.unrecorded') : ''))
</script>

<template>
  <AuditCard :title="tr('request.title')" :hint="hint" data-testid="observer-latest-request">
    <template v-if="request" #meta>
      <span :title="fullTime(request.started_at)">{{ clockTime(request.started_at) }}</span>
      <AuditId :id="request.request_id" />
    </template>
    <template v-if="request">
      <dl class="au-kv">
        <dt>{{ tr('request.input') }}</dt>
        <dd :title="input">{{ clip(input, 240) }}</dd>
        <dt>{{ tr('request.status') }}</dt>
        <dd class="au-chips">
          <Badge size="sm" :variant="variantOf(request.status)">{{ enumLabel('requestStatuses', request.status) }}</Badge>
          <span v-if="request.error_code" class="au-error" :title="request.error_code">{{ errorLabel(request.error_code) }}</span>
        </dd>
        <dt>{{ tr('request.elapsed') }}</dt>
        <dd>{{ duration(elapsedMs(request.started_at, request.completed_at)) }}</dd>
        <dt>{{ tr('request.cost') }}</dt>
        <dd>{{ cost }}</dd>
      </dl>
      <Table v-if="stages.length" :columns="columns" :rows="stages" row-key="key">
        <template #task="{ row }">{{ row.task || enumLabel('roles', row.role) }}</template>
        <template #model="{ row }">{{ row.model || '—' }}<span v-if="row.provider" class="au-muted"> · {{ row.provider }}</span></template>
        <template #elapsed="{ row }">{{ duration(row.elapsed_ms) }}</template>
        <template #status="{ row }">
          <Badge size="sm" :variant="variantOf(row.status)">{{ enumLabel('stageStatuses', row.status) }}</Badge>
          <span v-if="row.error_code" class="au-error" :title="row.error_code"> {{ errorLabel(row.error_code) }}</span>
        </template>
      </Table>
      <p v-else class="au-empty">{{ tr('empty') }}</p>
    </template>
    <p v-else class="au-empty">{{ tr('empty') }}</p>
  </AuditCard>
</template>
